import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Input, 
  FormControl, 
  FormLabel,
  LinearProgress,
  Alert,
  Sheet,
  Table,
  Modal,
  ModalDialog,
  ModalClose,
  Divider,
  Stack,
  CircularProgress,
  Autocomplete
} from '@mui/joy';
import {
  initializeDatabase,
  getDatabaseStats,
  searchIcd,
  uploadIcdFile,
  checkImportStatus,
  loadIcdDatabaseFromServer
} from '../utils/IcdDatabaseUtils';

// Import the ICD attribution component
import IcdAttribution from './IcdAttribution';

const IcdManager = () => {
  const [dbStatus, setDbStatus] = useState('checking');
  const [dbStats, setDbStats] = useState({ count: 0, lastUpdated: null, sourceFile: null, localCount: 0 });
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStatus, setImportStatus] = useState('idle');
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const [pollInterval, setPollInterval] = useState(null);
  const [importError, setImportError] = useState(null);
  
  const [loadingLocalDatabase, setLoadingLocalDatabase] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ status: 'idle', message: '', progress: 0 });
  
  // Initial database check and clean up poll interval on unmount
  useEffect(() => {
    checkDatabase();
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);
  
  const checkDatabase = async () => {
    setDbStatus('checking');
    try {
      // Initialize the database connection
      await initializeDatabase();
      
      // Check if the database has data
      const stats = await getDatabaseStats();
      setDbStats(stats);
      
      // Check if data is available locally or on server
      if (stats.localCount > 0) {
        setDbStatus('available');
      } else if (stats.count > 0) {
        setDbStatus('server-only');
      } else {
        setDbStatus('empty');
      }
    } catch (error) {
      console.error('Error checking ICD database:', error);
      setDbStatus('error');
    }
  };
  
  // New: safe wrapper to handle malformed / non-JSON server responses
  const safeLoadIcdDatabaseFromServer = async (force, progressCb) => {
    try {
      return await loadIcdDatabaseFromServer(force, progressCb);
    } catch (err) {
      if (err instanceof SyntaxError) {
        console.error('ICD load SyntaxError caught (likely invalid JSON / BOM / HTML response):', err);
        // Try probing one underlying endpoint (best‑effort) to detect response type
        try {
          const probe = await fetch('/api/icd/status', { headers: { 'Accept': 'application/json' } });
          const raw = await probe.text();
          const trimmed = raw.replace(/^\uFEFF/, '');
          let hint = '';
            if (raw.startsWith('<')) {
              hint = 'Der Server hat HTML statt JSON zurückgeliefert (evtl. Fehlerseite oder Proxy-Konfiguration).';
            } else if (raw.startsWith('\uFEFF')) {
              hint = 'Es scheint ein UTF-8 BOM vorhanden zu sein. Bitte entfernen Sie das BOM in der Server-Antwort.';
            } else if (/^[{[]/.test(trimmed) && raw !== trimmed) {
              hint = 'Ein führendes BOM wurde erkannt. Backend sollte es entfernen oder im Frontend vor dem Parsen strippen.';
            } else {
              hint = 'Antwort beginnt mit: ' + raw.slice(0, 40);
            }
          setLoadingProgress({
            status: 'error',
            message: `Ungültige JSON-Antwort vom Server. ${hint}`,
            progress: 100
          });
        } catch (probeErr) {
          console.warn('Zweitprobe der ICD-Endpunkte fehlgeschlagen:', probeErr);
        }
        return false;
      }
      throw err;
    }
  };

  // Add handler to manually load database to browser
  const handleLoadDatabaseToBrowser = async () => {
    try {
      setLoadingLocalDatabase(true);
      setLoadingProgress({ status: 'starting', message: 'Initialisiere Datenbank...', progress: 0 });
      
      const result = await safeLoadIcdDatabaseFromServer(true, progress => {
        setLoadingProgress(progress);
      });
      
      if (result) {
        await checkDatabase(); // Refresh database status
        setUploadResult({
          success: true,
          message: 'ICD Datenbank erfolgreich in den Browser geladen'
        });
      } else {
        setUploadResult({
          success: false,
          message: 'Fehler: Server-Antwort konnte nicht verarbeitet werden (kein gültiges JSON)'
        });
      }
    } catch (error) {
      console.error('Error loading database to browser:', error);
      setUploadResult({
        success: false,
        message: `Fehler: ${error.message}`
      });
    } finally {
      setLoadingLocalDatabase(false);
    }
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setUploadResult(null);
      setImportError(null);
      
      // Show file details
      console.log('Selected file:', {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        lastModified: new Date(selectedFile.lastModified).toLocaleString()
      });
    }
  };
  
  const handleFileUpload = async (e) => {
    // Prevent default form submission which could reload the page
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!file) {
      return;
    }
    
    // Reset states
    setImportError(null);
    
    // Open the import modal
    setShowImportModal(true);
    setImportStatus('uploading');
    setImportProgress({ current: 0, total: 100 });
    setUploadResult(null);
    setUploadProgress(0);
    
    try {
      // Start the upload
      setIsUploading(true);
      
      // Simulate upload progress
      const progressTimer = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) {
            return prev + 5;
          }
          clearInterval(progressTimer);
          return prev;
        });
      }, 300);
      
      // Upload the file to the server
      console.log('Starting file upload to server...');
      const result = await uploadIcdFile(file);
      clearInterval(progressTimer);
      setUploadProgress(100);
      
      console.log('Upload result:', result);
      
      if (result && result.success) {
        setImportStatus('processing');
        const importId = result.importId; // Use locally instead of in state
        
        // Start polling for import status
        const statusPoll = setInterval(async () => {
          try {
            const status = await checkImportStatus(importId);
            console.log('Import status:', status);
            
            if (status.status === 'completed') {
              clearInterval(statusPoll);
              setPollInterval(null);
              setImportStatus('completed');
              setUploadResult({
                success: true,
                message: `ICD-Datenbank erfolgreich aktualisiert. ${status.count} Einträge importiert.`,
                count: status.count
              });
              
              // Update database status
              await checkDatabase();
            }
            else if (status.status === 'error') {
              clearInterval(statusPoll);
              setPollInterval(null);
              setImportStatus('error');
              setImportError(status.error || 'Unbekannter Fehler beim Importieren der Daten');
              setUploadResult({
                success: false,
                message: status.error || 'Fehler beim Import der ICD-Datenbank'
              });
            }
            // Continue polling for 'processing' or 'idle' status
            
          } catch (error) {
            console.error('Error polling import status:', error);
            // Don't immediately fail - try again on next interval
            setImportError(`Fehler beim Abrufen des Import-Status: ${error.message}`);
          }
        }, 2000); // Poll every 2 seconds
        
        setPollInterval(statusPoll);
      } else {
        // Handle server response that indicates failure but doesn't throw an error
        setImportStatus('error');
        setImportError(result?.error || 'Unerwarteter Server-Fehler');
        setUploadResult({
          success: false,
          message: result?.error || 'Der Server konnte die Datei nicht verarbeiten'
        });
        setUploadProgress(0);
      }
    } catch (error) {
      console.error('Error uploading ICD file:', error);
      setImportStatus('error');
      setImportError(error.message || 'Netzwerkfehler beim Hochladen');
      setUploadResult({
        success: false,
        message: `Fehler beim Hochladen der ICD-Datei: ${error.message}`
      });
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleSearch = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const results = await searchIcd(query, { limit: 50 });
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching ICD:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unbekannt';
    
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return dateStr;
    }
  };

  const formatFileSize = (size) => {
    if (!size) return 'Unbekannt';
    
    const kb = size / 1024;
    if (kb < 1000) {
      return `${kb.toFixed(2)} KB`;
    }
    
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <Box>
      <Typography level="h2" sx={{ mb: 2 }}>ICD-10 Datenbank verwalten</Typography>
      
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="h3">Datenbankstatus</Typography>
          
          {dbStatus === 'checking' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <CircularProgress size="sm" />
              <Typography>Überprüfe Datenbank...</Typography>
            </Box>
          )}
          
          {dbStatus === 'empty' && (
            <Alert color="warning" sx={{ mt: 2 }}>
              Die ICD-Datenbank ist leer. Bitte laden Sie eine ICD-10 Datei hoch.
            </Alert>
          )}
          
          {dbStatus === 'error' && (
            <Alert color="danger" sx={{ mt: 2 }}>
              Fehler beim Überprüfen der ICD-Datenbank.
            </Alert>
          )}
          
          {dbStatus === 'available' && (
            <Box sx={{ mt: 2 }}>
              <Typography>
                Status: <strong style={{ color: 'green' }}>Verfügbar im Browser</strong>
              </Typography>
              <Typography>
                Lokale Einträge: <strong>{dbStats.localCount?.toLocaleString() || 0}</strong>
              </Typography>
              <Typography>
                Letzte Aktualisierung: <strong>{formatDate(dbStats.lastUpdated)}</strong>
              </Typography>
              {dbStats.sourceFile && (
                <Typography>
                  Quelldatei: <strong>{dbStats.sourceFile}</strong> 
                  {dbStats.fileSize && ` (${formatFileSize(dbStats.fileSize)})`}
                </Typography>
              )}
            </Box>
          )}
          
          {dbStatus === 'server-only' && (
            <Box sx={{ mt: 2 }}>
              <Typography>
                Status: <strong style={{ color: 'orange' }}>Verfügbar auf Server</strong>
              </Typography>
              <Typography>
                Server-Einträge: <strong>{dbStats.count.toLocaleString()}</strong>
              </Typography>
              <Typography>
                Letzte Aktualisierung: <strong>{formatDate(dbStats.lastUpdated)}</strong>
              </Typography>
              {dbStats.sourceFile && (
                <Typography>
                  Quelldatei: <strong>{dbStats.sourceFile}</strong> 
                  {dbStats.fileSize && ` (${formatFileSize(dbStats.fileSize)})`}
                </Typography>
              )}
              
              <Box sx={{ mt: 2 }}>
                <Button
                  color="primary"
                  onClick={handleLoadDatabaseToBrowser}
                  loading={loadingLocalDatabase}
                  disabled={loadingLocalDatabase}
                >
                  Datenbank in Browser laden
                </Button>
              </Box>
              
              {loadingLocalDatabase && (
                <Box sx={{ mt: 2, width: '100%' }}>
                  <Typography level="body-sm" mb={1}>
                    {loadingProgress.message || 'Laden...'}
                  </Typography>
                  <LinearProgress 
                    determinate={loadingProgress.progress > 0} 
                    value={loadingProgress.progress} 
                  />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 2 }}>ICD-10 Datei hochladen</Typography>
          
          <Typography sx={{ mb: 2 }}>
            Laden Sie eine ICD-10 Textdatei hoch, um die Datenbank zu aktualisieren.
            Die neue Datei wird die bestehende Datenbank ersetzen.
            Die Datei sollte im Format ICD10GM sein.
          </Typography>
          
          <form onSubmit={handleFileUpload}>
            <FormControl>
              <FormLabel>ICD-10 Datei auswählen (TXT-Format)</FormLabel>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <Input
                  type="file"
                  accept=".txt"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  slotProps={{
                    input: {
                      'aria-label': 'ICD-10 Datei auswählen'
                    }
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  type="submit"
                  color="primary"
                  disabled={!file || isUploading}
                  loading={isUploading}
                  onClick={handleFileUpload}
                >
                  {isUploading ? 'Wird hochgeladen...' : 'Datei importieren'}
                </Button>
              </Box>
            </FormControl>
          </form>
          
          {file && !isUploading && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: '4px' }}>
              <Typography level="body-sm">
                <strong>Ausgewählte Datei:</strong> {file.name}
              </Typography>
              <Typography level="body-sm">
                <strong>Größe:</strong> {formatFileSize(file.size)}
              </Typography>
              <Typography level="body-sm">
                <strong>Zuletzt geändert:</strong> {new Date(file.lastModified).toLocaleString()}
              </Typography>
            </Box>
          )}
          
          {isUploading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress 
                determinate 
                value={uploadProgress} 
                sx={{ width: '100%', mb: 1 }}
              />
              <Typography level="body-sm" textAlign="center">
                {uploadProgress.toFixed(0)}% abgeschlossen
              </Typography>
            </Box>
          )}
          
          {uploadResult && (
            <Alert 
              color={uploadResult.success ? 'success' : 'danger'}
              sx={{ mt: 2 }}
            >
              {uploadResult.message}
            </Alert>
          )}
          
          {importError && (
            <Alert color="danger" sx={{ mt: 2 }}>
              {importError}
            </Alert>
          )}
        </CardContent>
      </Card>
      
      <Card variant="outlined">
        <CardContent>
          <Typography level="h3" sx={{ mb: 2 }}>ICD-10 Suche</Typography>
          
          <Typography sx={{ mb: 2 }}>
            Suchen Sie nach ICD-10 Codes oder Beschreibungen, um die Datenbank zu testen.
            {dbStatus === 'available' && " Die Suche erfolgt lokal im Browser."}
            {dbStatus === 'server-only' && " Die Suche erfolgt auf dem Server."}
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              placeholder="ICD-10 Code oder Beschreibung eingeben"
              loading={isSearching}
              value={searchQuery}
              onChange={(event, newValue) => {
                setSearchQuery(newValue);
                handleSearch(newValue);
              }}
              onInputChange={(event, newValue) => {
                setSearchQuery(newValue);
                handleSearch(newValue);
              }}
              options={[]}
              freeSolo
              sx={{ width: '100%' }}
            />
          </Box>
          
          {searchResults.length > 0 && (
            <Sheet sx={{ height: '400px', overflow: 'auto' }}>
              <Table stripe="odd" hoverRow>
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>ICD-10 Code</th>
                    <th>Beschreibung</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((result) => (
                    <tr key={result.code}>
                      <td>{result.code}</td>
                      <td>{result.text}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          )}
          
          {searchQuery && searchResults.length === 0 && !isSearching && (
            <Typography sx={{ textAlign: 'center', p: 2, color: 'text.secondary' }}>
              Keine Ergebnisse gefunden
            </Typography>
          )}
          
          {isSearching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          )}
          
          {/* Replace the existing attribution notice with the reusable component */}
          <IcdAttribution />
        </CardContent>
      </Card>
      
      {/* Import Progress Modal */}
      <Modal 
        open={showImportModal} 
        onClose={() => !isUploading && importStatus !== 'processing' && setShowImportModal(false)}
      >
        <ModalDialog sx={{ maxWidth: 500, width: '100%' }}>
          {!isUploading && importStatus !== 'processing' && <ModalClose />}
          <Typography level="h4">ICD-10 Import</Typography>
          <Divider sx={{ my: 2 }} />
          
          <Stack spacing={2}>
            {importStatus === 'uploading' && (
              <>
                <Typography>Datei wird zum Server hochgeladen...</Typography>
                <LinearProgress 
                  determinate 
                  value={uploadProgress} 
                />
                <Typography level="body-sm">
                  Bitte warten Sie, während die Datei hochgeladen wird.
                </Typography>
              </>
            )}
            
            {importStatus === 'processing' && (
              <>
                <Typography>Der Server verarbeitet die ICD-10 Datei...</Typography>
                <LinearProgress />
                <Typography level="body-sm">
                  Die ICD-10 Daten werden auf dem Server verarbeitet und in die Datenbank importiert.
                  Dieser Vorgang kann je nach Dateigröße einige Zeit dauern.
                </Typography>
                <Typography level="body-sm">
                  Sie können das Fenster schließen - der Import läuft im Hintergrund weiter.
                  Der aktuelle Status wird beim nächsten Laden der Seite angezeigt.
                  {importProgress.total > 0 && ` Fortschritt: ${importProgress.current}/${importProgress.total}`}
                </Typography>
              </>
            )}
            
            {importStatus === 'completed' && (
              <>
                <Alert color="success">
                  {uploadResult?.message || 'Import abgeschlossen'}
                </Alert>
                <Typography level="body-sm">
                  Die ICD-10 Datenbank wurde erfolgreich aktualisiert. Sie können nun die Codes und Beschreibungen über die Suchfunktion abfragen.
                </Typography>
              </>
            )}
            
            {importStatus === 'error' && (
              <>
                <Alert color="danger">
                  {uploadResult?.message || 'Fehler beim Import'}
                </Alert>
                <Typography level="body-sm">
                  Es ist ein Fehler beim Import der ICD-10 Daten aufgetreten.
                  Bitte überprüfen Sie das Dateiformat und versuchen Sie es erneut.
                </Typography>
                {importError && (
                  <Typography level="body-sm" color="danger">
                    Details: {importError}
                  </Typography>
                )}
              </>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button 
                disabled={isUploading || importStatus === 'processing'} 
                onClick={() => setShowImportModal(false)}
              >
                {importStatus === 'completed' || importStatus === 'error' ? 'Schließen' : 'Abbrechen'}
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default IcdManager;
